/**
 * Integrytas Group - Base App (MOBILE)
 * Page: IAM.PROVIDERS — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/IamProviderManagerPage.tsx. A DISTINCT
 * presentation (dense card list + facet chips + bottom-sheet detail + edit sheet)
 * over the SAME data/logic layer only:
 *   - reads via apiDb (iam_provider, plus csm_role_master + _tl to resolve the
 *     default-role FK to a translated name for display — §17.B4, never a raw id),
 *   - every write (metadata edit: name / type / default role / JIT / enabled)
 *     routes through the Common Routine (routedUpdate) — §2 single write path;
 *     never a direct apiDb write.
 *
 * Secrets stay on the desktop / in GoTrue server config: the mobile screen never
 * surfaces or edits provider client secrets. The GoTrue provider key is shown
 * read-only ("edit on desktop"), mirroring MobileUserEditSheet's read-only roles.
 * It never re-renders the desktop grid; §16.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Power, X, SquarePen, Fingerprint } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { routedUpdate } from '../../lib/routed_writes';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import { MobileRelatedChildren } from '../components/MobileRelatedChildren';
import type { AuthSession } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'IAM.PROVIDERS';

const JIT_FACET = '__jit__';

// §17.B11: CHECK-enum values defined once as a labelled constant — the stored
// value stays the code, only the displayed label is localised via t().
const PROVIDER_TYPES: { value: string; labelKey: string; fallback: string }[] = [
  { value: 'OAUTH', labelKey: 'mobile.iam.type.OAUTH', fallback: 'OAuth' },
  { value: 'OIDC', labelKey: 'mobile.iam.type.OIDC', fallback: 'OIDC' },
  { value: 'SAML', labelKey: 'mobile.iam.type.SAML', fallback: 'SAML' },
  { value: 'PASSWORD', labelKey: 'mobile.iam.type.PASSWORD', fallback: 'Password' },
];

interface MappingLite {
  iam_role_mapping_id: number;
  iam_provider_id: number;
  match_type: string;
  match_key: string | null;
  match_value: string | null;
  role_id: number | null;
}

interface MobileProviderRow {
  iam_provider_id: number;
  internal_code: string;
  name: string;
  provider_type: string;
  gotrue_provider: string | null;
  auto_provision: 'Y' | 'N';
  default_role_id: number | null;
  is_active: 'Y' | 'N';
  default_role_label: string; // resolved translated role name, '' when unset
}

interface RoleOption {
  role_id: number;
  label: string;
}

type StatusFacet = 'all' | 'active' | 'inactive';

interface TlRow { locale_code: string; display_text: string }

export function MobileIamProviderManagerPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const [providers, setProviders] = useState<MobileProviderRow[]>([]);
  // §17.B13 relation: the role mappings each provider owns, as the desktop shows.
  const [mappings, setMappings] = useState<MappingLite[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [jitOnly, setJitOnly] = useState(false);
  const [selected, setSelected] = useState<MobileProviderRow | null>(null);
  const [editing, setEditing] = useState<MobileProviderRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      // Resolve role codes to translated names (§17.B4 — never the raw role id).
      const { data: rawRoles, error: rErr } = await apiDb
        .from('csm_role_master')
        .select('role_id, internal_code, csm_role_master_tl ( locale_code, display_text )')
        .order('internal_code');
      if (rErr) throw rErr;

      type RawRole = { role_id: number; internal_code: string; csm_role_master_tl?: TlRow[] };
      const roleLabelById: Record<number, string> = {};
      const roleOptions: RoleOption[] = ((rawRoles ?? []) as RawRole[]).map((r) => {
        const tls = r.csm_role_master_tl ?? [];
        const exact = tls.find((tl) => tl.locale_code === activeLocale);
        const fb = tls.find((tl) => tl.locale_code === 'EN_US') ?? tls[0];
        const label = exact?.display_text ?? fb?.display_text ?? r.internal_code;
        roleLabelById[r.role_id] = label;
        return { role_id: r.role_id, label };
      });
      setRoles(roleOptions);

      const { data: rawProviders, error: pErr } = await apiDb
        .from('iam_provider')
        .select('iam_provider_id, internal_code, name, provider_type, gotrue_provider, auto_provision, default_role_id, is_active')
        .order('internal_code');
      if (pErr) throw pErr;

      type RawProvider = Omit<MobileProviderRow, 'default_role_label'>;
      const rows: MobileProviderRow[] = ((rawProviders ?? []) as RawProvider[]).map((p) => ({
        ...p,
        default_role_label: p.default_role_id == null ? '' : (roleLabelById[p.default_role_id] ?? ''),
      }));
      setProviders(rows);

      const { data: mapRows } = await apiDb
        .from('iam_role_mapping')
        .select('iam_role_mapping_id, iam_provider_id, match_type, match_key, match_value, role_id')
        .order('iam_role_mapping_id');
      setMappings((mapRows ?? []) as MappingLite[]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (role labels follow it).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  const typeLabel = (value: string): string => {
    const def = PROVIDER_TYPES.find((x) => x.value === value);
    return def ? t(def.labelKey, def.fallback) : value;
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return providers.filter((p) => {
      if (statusFacet === 'active' && p.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && p.is_active !== 'N') return false;
      if (jitOnly && p.auto_provision !== 'Y') return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        p.internal_code.toLowerCase().includes(q) ||
        (p.gotrue_provider ?? '').toLowerCase().includes(q)
      );
    });
  }, [providers, filter, statusFacet, jitOnly]);

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.iam.all', 'All') },
    { key: 'active', label: t('common.boolean.enabled', 'Enabled') },
    { key: 'inactive', label: t('common.boolean.disabled', 'Disabled') },
  ];

  const enabledPill = (v: 'Y' | 'N') => (
    <MobileStatusPill tone={v === 'Y' ? 'ok' : 'no'}>
      {v === 'Y' ? t('common.boolean.enabled', 'Enabled') : t('common.boolean.disabled', 'Disabled')}
    </MobileStatusPill>
  );

  const toggleActive = async (p: MobileProviderRow) => {
    setActionBusy(true);
    setError(null);
    const next = p.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'iam_provider', 'iam_provider_id', p.iam_provider_id, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.iam.title', 'Identity Providers')}
        description={t('mobile.iam.description', 'Sign-in providers, their default roles and just-in-time provisioning.')}
      />

      <MobileStatStrip
        stats={[
          { value: providers.length, label: t('mobile.iam.stat.total', 'Total') },
          { value: providers.filter((p) => p.is_active === 'Y').length, label: t('common.boolean.enabled', 'Enabled') },
          { value: providers.filter((p) => p.auto_provision === 'Y').length, label: t('mobile.iam.jit', 'JIT') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.iam.search', 'Search providers')}
          aria-label={t('mobile.iam.search', 'Search providers')}
        />
      </div>

      <MobileFilterChips
        chips={[
          ...statusFacets.map((f) => ({ value: f.key, label: f.label })),
          { value: JIT_FACET, label: t('mobile.iam.jit', 'JIT') },
        ]}
        isActive={(v) => (v === JIT_FACET ? jitOnly : statusFacet === v)}
        onSelect={(v) => { if (v === JIT_FACET) { setJitOnly((x) => !x); } else { setStatusFacet(v as StatusFacet); } }}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.iam.none', 'No providers match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((p) => (
            <MobileCardRow
              key={p.iam_provider_id}
              lead={<Fingerprint />}
              leadTone={p.is_active === 'Y' ? 'blue' : 'slate'}
              title={p.name || p.internal_code}
              subtitle={
                typeLabel(p.provider_type)
                + (p.gotrue_provider ? ' · ' + p.gotrue_provider : '')
                + (p.auto_provision === 'Y' ? ' · ' + t('mobile.iam.jit', 'JIT') : '')
              }
              trailing={enabledPill(p.is_active)}
              chevron
              onClick={() => setSelected(p)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.iam.detail', 'Provider detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Fingerprint size={20} /></span>
              <span>
                <div className="m-nm">{selected.name || selected.internal_code}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow label={t('mobile.iam.status', 'Status')} value={enabledPill(selected.is_active)} />
              <MobileDetailRow label={t('mobile.iam.type_lbl', 'Type')} value={typeLabel(selected.provider_type)} />
              <MobileDetailRow
                label={t('mobile.iam.gotrue_key', 'GoTrue key')}
                value={selected.gotrue_provider || t('common.none', 'None')}
              />
              <MobileDetailRow
                label={t('mobile.iam.default_role', 'Default role')}
                value={selected.default_role_label || t('mobile.iam.no_default_role', 'No default role')}
              />
              <MobileDetailRow
                label={t('mobile.iam.jit_provisioning', 'JIT provisioning')}
                value={
                  <MobileStatusPill tone={selected.auto_provision === 'Y' ? 'ok' : 'no'}>
                    {selected.auto_provision === 'Y' ? t('common.boolean.on', 'On') : t('common.boolean.off', 'Off')}
                  </MobileStatusPill>
                }
              />
            </MobileContentCard>
            {/* §17.B13 — the row's related records, stacked one group per block. */}
            <MobileContentCard>
              <MobileRelatedChildren
                session={session}
                groups={[
                  {
                    key: 'mappings',
                    table: 'iam_role_mapping', pkColumn: 'iam_role_mapping_id',
                    title: t('mobile.iam.related.mappings', 'Role Mappings'),
                    items: mappings
                      .filter(mp => mp.iam_provider_id === selected.iam_provider_id)
                      .map(mp => ({
                        id: mp.iam_role_mapping_id,
                        label: mp.match_value || mp.match_type,
                        sub: `${mp.match_type}${mp.match_key ? ` ${mp.match_key}` : ''}`
                          + (mp.role_id == null ? '' : ` → ${roles.find(r => r.role_id === mp.role_id)?.label ?? ''}`),
                      })),
                  },
                ]}
              />
            </MobileContentCard>
            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className="m-btn m-btn-primary"
                disabled={actionBusy}
                onClick={() => { setEditing(selected); setSelected(null); }}
              >
                <SquarePen size={16} />
                {t('mobile.iam.edit', 'Edit')}
              </button>)}
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                disabled={actionBusy}
                onClick={() => void toggleActive(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('mobile.iam.disable', 'Disable') : t('mobile.iam.enable', 'Enable')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <MobileIamProviderEditSheet
        session={session}
        provider={editing}
        roles={roles}
        typeLabel={typeLabel}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Edit sheet — the provider's non-secret metadata (name, type, default role,
// JIT provisioning, enabled), saved through the Common Routine (routedUpdate)
// — §2. The GoTrue provider key is paired with the server-side client secret,
// so it is shown read-only here ("edit on desktop"); secrets never reach mobile.
// ---------------------------------------------------------------------------

interface MobileIamProviderEditSheetProps {
  session: AuthSession;
  provider: MobileProviderRow | null;
  roles: RoleOption[];
  typeLabel: (value: string) => string;
  onClose: () => void;
  onSaved: () => void;
}

function MobileIamProviderEditSheet({ session, provider, roles, typeLabel, onClose, onSaved }: MobileIamProviderEditSheetProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [providerType, setProviderType] = useState('OAUTH');
  const [defaultRoleId, setDefaultRoleId] = useState<number | null>(null);
  const [autoProvision, setAutoProvision] = useState(false);
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different provider is opened.
  useEffect(() => {
    if (!provider) return;
    setName(provider.name);
    setProviderType(provider.provider_type);
    setDefaultRoleId(provider.default_role_id);
    setAutoProvision(provider.auto_provision === 'Y');
    setActive(provider.is_active === 'Y');
    setError(null);
  }, [provider]);

  const save = async () => {
    if (!provider) return;
    if (!name.trim()) { setError(t('mobile.iam.name_required', 'Name is required.')); return; }
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'iam_provider', 'iam_provider_id', provider.iam_provider_id, {
      name: name.trim(),
      provider_type: providerType,
      default_role_id: defaultRoleId,
      auto_provision: autoProvision ? 'Y' : 'N',
      is_active: active ? 'Y' : 'N',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = provider !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.iam.edit_sheet', 'Edit provider')}>
        <div className="m-grabber" />
        {provider && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Fingerprint size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.iam.edit_title', 'Edit provider')}</div>
                <div className="m-em">{provider.internal_code}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-iam-name">{t('mobile.iam.name', 'Name')}</label>
                <input
                  id="m-iam-name"
                  className="m-inp"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-iam-type">{t('mobile.iam.type_lbl', 'Type')}</label>
                <select id="m-iam-type" className="m-sel" value={providerType} onChange={(e) => setProviderType(e.target.value)}>
                  {PROVIDER_TYPES.map((o) => <option key={o.value} value={o.value}>{typeLabel(o.value)}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-iam-role">{t('mobile.iam.default_role', 'Default role')}</label>
                <select
                  id="m-iam-role"
                  className="m-sel"
                  value={defaultRoleId == null ? '' : String(defaultRoleId)}
                  onChange={(e) => setDefaultRoleId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">{t('mobile.iam.no_default_role', 'No default role')}</option>
                  {roles.map((r) => <option key={r.role_id} value={String(r.role_id)}>{r.label}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label>{t('mobile.iam.gotrue_key', 'GoTrue key')}</label>
                <div className="m-formrow-ro">
                  {provider.gotrue_provider || t('common.none', 'None')}
                  <span className="m-formrow-hint"> · {t('mobile.iam.secret_desktop', 'secret / key edited on desktop')}</span>
                </div>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('mobile.iam.jit_provisioning', 'JIT provisioning')}</div>
                  <div className="m-formrow-s">{t('mobile.iam.jit_hint', 'Create users on first sign-in')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={autoProvision}
                  className={'m-switch ' + (autoProvision ? 'm-on' : 'm-off')}
                  onClick={() => setAutoProvision((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.enabled', 'Enabled')}</div>
                  <div className="m-formrow-s">{t('mobile.iam.enabled_hint', 'Available for sign-in')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
